/**
 * Nutrition Modifiers
 *
 * Types inferred from JSON data. Slugs and categories are literal types,
 * descriptive fields (name, description, note) are strings.
 */

import modifiersJson from "./data/modifiers.json";

// Category literal union inferred from JSON keys
export type ModifierCategory = keyof typeof modifiersJson;

// Per-category slug literal unions
export type CookingModifierSlug =
  (typeof modifiersJson.cooking)[number]["slug"];
export type PreparationModifierSlug =
  (typeof modifiersJson.preparation)[number]["slug"];
export type AdditionModifierSlug =
  (typeof modifiersJson.addition)[number]["slug"];

// Combined modifier slug union
export type NutritionModifierSlug =
  | CookingModifierSlug
  | PreparationModifierSlug
  | AdditionModifierSlug;

// Modifier effect structure (all numeric fields)
export interface ModifierEffect {
  yieldMultiplier?: number;
  fatAdd?: number;
  fatMultiplier?: number;
  carbsAdd?: number;
  proteinAdd?: number;
  fiberAdd?: number;
  saturatedFatAdd?: number;
  sodiumAdd?: number;
}

// Modifier rule structure
export interface ModifierRule {
  tagMatch: string[];
  effects: ModifierEffect;
  note?: string;
}

// Runtime interface with widened string fields
export interface ModifierDefinition {
  slug: NutritionModifierSlug;
  name: string;
  category: ModifierCategory;
  description: string;
  exclusiveGroup?: string;
  rules: ModifierRule[];
  defaultEffects: ModifierEffect;
}

export const COOKING_MODIFIERS: ModifierDefinition[] =
  modifiersJson.cooking as ModifierDefinition[];
export const PREPARATION_MODIFIERS: ModifierDefinition[] =
  modifiersJson.preparation as ModifierDefinition[];
export const ADDITION_MODIFIERS: ModifierDefinition[] =
  modifiersJson.addition as ModifierDefinition[];

export const ALL_MODIFIERS: ModifierDefinition[] = [
  ...COOKING_MODIFIERS,
  ...PREPARATION_MODIFIERS,
  ...ADDITION_MODIFIERS,
];

const MODIFIER_BY_SLUG = ALL_MODIFIERS.reduce(
  (acc, modifier) => {
    acc[modifier.slug] = modifier;
    return acc;
  },
  {} as Record<NutritionModifierSlug, ModifierDefinition>
);

export function getModifierDefinition(
  slug: NutritionModifierSlug
): ModifierDefinition | undefined {
  return MODIFIER_BY_SLUG[slug];
}

export function getModifiersByCategory(
  category: ModifierCategory
): ModifierDefinition[] {
  switch (category) {
    case "cooking":
      return COOKING_MODIFIERS;
    case "preparation":
      return PREPARATION_MODIFIERS;
    case "addition":
      return ADDITION_MODIFIERS;
    default: {
      const _exhaustive: never = category;
      return _exhaustive;
    }
  }
}

export function isNutritionModifierSlug(
  value: string
): value is NutritionModifierSlug {
  return value in MODIFIER_BY_SLUG;
}
